import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { NavLink, useMatch, useSearchParams } from "react-router";

import { IconButton } from "../components/ui/IconButton";
import { ChevronLeftIcon, ChevronRightIcon, ClockIcon, EyeOffIcon } from "../components/ui/Icons";
import { addDays, formatLongDay, formatShortDay } from "../lib/dates";
import { privacyDemo, setPrivacy, usePrivacy } from "../lib/privacy";
import { tabsFor } from "./navigation";
import { useSession } from "./session-context";
import { useSelectedDay } from "./useSelectedDay";
import { useViewedUserId } from "./useViewedUser";
import { UserMenu } from "./UserMenu";

function DateNav() {
  const { day, today } = useSelectedDay();
  const [, setSearchParams] = useSearchParams();
  // Only the date changes: the owner's chosen user stays.
  const go = (next: string) => {
    setSearchParams((current) => {
      const params = new URLSearchParams(current);
      if (next === today) {
        params.delete("date");
      } else {
        params.set("date", next);
      }
      return params;
    });
  };
  // Until whose day it is is known (#862).
  if (day === undefined) {
    return null;
  }

  return (
    <div className="flex items-center gap-1">
      <IconButton
        aria-label="Previous day"
        onClick={() => {
          go(addDays(day, -1));
        }}
      >
        <ChevronLeftIcon />
      </IconButton>
      <span className="px-1.5 text-sm font-semibold" aria-label={formatLongDay(day)}>
        {formatShortDay(day)}
      </span>
      <IconButton
        aria-label="Next day"
        onClick={() => {
          go(addDays(day, 1));
        }}
      >
        <ChevronRightIcon />
      </IconButton>
      {today !== undefined && day !== today ? (
        <button
          type="button"
          className="ml-1 h-10 cursor-pointer rounded-pill border border-line px-4 text-sm font-medium hover:bg-panel-2"
          onClick={() => {
            go(today);
          }}
        >
          Today
        </button>
      ) : null}
    </div>
  );
}

/** The owner's user switcher (section 11.2): everyone, including other owners. */
function UserSwitcher() {
  const { api, me } = useSession();
  const [, setSearchParams] = useSearchParams();
  const selected = useViewedUserId() ?? me.id;
  const users = useQuery({
    queryKey: ["users"],
    queryFn: () => api.listUsers(),
    staleTime: 5 * 60_000,
  });

  return (
    <label className="flex items-center">
      <span className="sr-only">Show the day of</span>
      <select
        value={selected}
        disabled={users.data === undefined}
        onChange={(event) => {
          const id = event.target.value;
          setSearchParams((current) => {
            const params = new URLSearchParams(current);
            if (id === me.id) {
              params.delete("user");
            } else {
              params.set("user", id);
            }
            return params;
          });
        }}
        className="h-10 max-w-60 cursor-pointer rounded-pill border border-line bg-panel pr-8 pl-4 text-sm font-medium text-text disabled:cursor-default"
      >
        {users.data === undefined ? (
          <option value={selected}>{me.display_name ?? me.email ?? "You"}</option>
        ) : (
          users.data.map((user) => (
            <option key={user.id} value={user.id}>
              {(user.display_name ?? user.email ?? "Unnamed user") +
                (user.id === me.id ? " (you)" : "") +
                (user.is_active ? "" : " (inactive)")}
            </option>
          ))
        )}
      </select>
    </label>
  );
}

/**
 * Privacy mode's button (the owner, 2026-10-11; ADRs 0144 and 0145): made-up
 * client data on every screen, in a demo copy where everything works and
 * nothing reaches the real data or Autotask. First in the header's right-hand
 * group: left of the user switcher for an owner, of the date for a tech, of
 * the avatar elsewhere. Turning it on hides the real data at once, the screens
 * waiting for the copy; one that can't be made turns it off again, saying why.
 * Every screen loads again on any change (usePrivacyReload).
 */
function PrivacyToggle() {
  const on = usePrivacy();
  const { api } = useSession();
  const [making, setMaking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function toggle() {
    setError(null);
    if (on) {
      const demo = privacyDemo();
      setPrivacy(null);
      if (demo !== null) {
        // Not dropped now: it goes when it's a day old.
        api.dropDemoCopy(demo).catch(() => undefined);
      }
      return;
    }
    // On at once: the real data goes from every screen, which wait for the copy.
    setPrivacy("on");
    setMaking(true);
    try {
      await api.startDemo();
    } catch (e) {
      setPrivacy(null);
      setError(e instanceof Error ? e.message : "The demo copy couldn't be made.");
    } finally {
      setMaking(false);
    }
  }

  return (
    <div className="relative flex items-center">
      <button
        type="button"
        aria-pressed={on}
        aria-busy={making}
        aria-label="Privacy mode"
        disabled={making}
        title={
          making
            ? "Making a demo copy…"
            : on
              ? "Privacy mode is on: made-up client data, in a demo copy where nothing reaches the real data or Autotask. Click to show the real data."
              : "Privacy mode: made-up client data, in a demo copy where everything works, for a demo"
        }
        onClick={() => {
          void toggle();
        }}
        className={
          "flex size-10 cursor-pointer items-center justify-center rounded-pill border disabled:cursor-wait " +
          (on
            ? "border-transparent bg-brand-tint text-brand-ink-on-tint"
            : "border-line bg-panel text-muted hover:text-text") +
          (making ? " animate-pulse" : "")
        }
      >
        <EyeOffIcon />
      </button>
      {error === null ? null : (
        <p
          role="alert"
          className="absolute top-full right-0 z-10 mt-1.5 w-64 rounded-row border border-line bg-panel px-3 py-2 text-xs text-status-needs-client-fg shadow-sm"
        >
          {`Privacy mode didn't start: ${error}`}
        </p>
      )}
    </div>
  );
}

/** The header panel (section 11.0): logo, role-filtered tabs, date navigation, avatar.
 *
 * One 60 px row where it fits. Where it doesn't (a half-screen window, a
 * tablet: BUILD_PLAN 11.3), the right-hand group wraps onto a second row
 * instead of pushing the page sideways (#219). On a phone, the tabs wrap too. */
export function Header() {
  const { me } = useSession();
  const onDayView = useMatch("/") !== null;

  return (
    <header className="flex min-h-[60px] shrink-0 flex-wrap items-center gap-x-8 gap-y-2 rounded-panel border border-line bg-panel py-[11px] pr-3 pl-4">
      <div className="flex items-center gap-2.5">
        <div className="flex size-[30px] items-center justify-center rounded-icon bg-brand text-white">
          <ClockIcon />
        </div>
        <span className="text-[17px] font-bold tracking-[-0.02em]">Axon Time</span>
      </div>
      {/* An owner's six tabs are wider than a phone: there they wrap,
          inside the header, rather than push the page sideways. */}
      <nav aria-label="Main" className="flex min-w-0 flex-wrap gap-1">
        {tabsFor(me.role).map((tab) => (
          <NavLink
            key={tab.path}
            to={tab.path}
            // Today is "/", the prefix of every path; the others own their sub-pages.
            end={tab.path === "/"}
            className={({ isActive }) =>
              "flex h-[38px] items-center rounded-pill px-4 text-sm " +
              (isActive
                ? "bg-brand-tint font-semibold text-brand-ink-on-tint"
                : "font-medium text-muted hover:text-text")
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>
      <div className="ml-auto flex flex-wrap items-center justify-end gap-2.5">
        <PrivacyToggle />
        {onDayView && me.role === "owner" ? <UserSwitcher /> : null}
        {onDayView ? <DateNav /> : null}
        <span aria-hidden="true" className="mx-1.5 h-7 w-px bg-line" />
        <UserMenu />
      </div>
    </header>
  );
}
